/**
 * Whose speech-rate alerts a session's rep summary shows (2026-10-05).
 *
 * Every rep has their own speech-rate monitor, and each saved alert names its
 * producer (`participantId`). The call's summary (Call Insights' speed card,
 * the Statistics count) is the owner's:
 *
 *  - a standalone desktop call: the creator's alerts (`salesperson`) plus
 *    legacy alerts with no id; a co-seller's alerts are theirs, never the owner's;
 *  - a bot call: the alerts of the roster row the services marked as the owner
 *    (`externalId` = the owner's Firebase uid = the session's `userId`). A call
 *    saved before that mark has no such row and keeps every alert.
 *
 * Same rule as salesguru-backend's `src/utils/speechRateAlerts.js`.
 */
import { MERGEABLE_SESSION_SOURCE } from '../constants/participantMerge';
import { STANDALONE_OWNER_PARTICIPANT_ID } from '../components/session/participantMerge';

/** The producer field every saved alert may carry. */
export interface ProducedSpeechRateAlert {
  participantId?: string | number | null;
}

/** What of the session decides whose alerts are the owner's. */
export interface SpeechRateOwnerScope {
  /** `session.metadata.source`. */
  source?: string | null;
  /** The owner's Firebase uid (`session.userId`). */
  userId?: string | null;
  participants?: Array<{ participantId?: string | number | null; externalId?: string | number | null } | null> | null;
}

const hasNoProducer = (participantId: unknown): boolean =>
  participantId === undefined || participantId === null || participantId === '';

/** Whether an alert is the standalone creator's, or a legacy alert with no producer. */
export const isOwnerSpeechRateAlert = (alert: ProducedSpeechRateAlert | null | undefined): boolean =>
  hasNoProducer(alert?.participantId) || String(alert?.participantId) === STANDALONE_OWNER_PARTICIPANT_ID;

/** The ids (as strings) of the roster rows marked as a bot call's owner, or null when none is marked. */
export function botOwnerParticipantIds(scope: SpeechRateOwnerScope | null | undefined): Set<string> | null {
  const ownerUid = scope?.userId;
  if (!ownerUid) return null;
  const ownerIds = (scope?.participants ?? [])
    .filter((row) => row && !hasNoProducer(row.participantId) && String(row.externalId) === String(ownerUid))
    .map((row) => String(row?.participantId));
  return ownerIds.length > 0 ? new Set(ownerIds) : null;
}

/** The alerts that are the call owner's (every alert on an unmarked bot call). */
export function ownerSpeechRateAlerts<T extends ProducedSpeechRateAlert>(
  alerts: T[] | null | undefined,
  scope: SpeechRateOwnerScope | null | undefined,
): T[] {
  const all = alerts ?? [];
  if (scope?.source === MERGEABLE_SESSION_SOURCE) return all.filter(isOwnerSpeechRateAlert);
  const ownerIds = botOwnerParticipantIds(scope);
  if (!ownerIds) return all;
  return all.filter((alert) => !hasNoProducer(alert?.participantId) && ownerIds.has(String(alert.participantId)));
}

/** The owner's alert count: on an unmarked bot call the stored total, as before. */
export function ownerSpeechRateAlertCount(
  analysis: { alerts?: ProducedSpeechRateAlert[] | null; totalAlerts?: number | null } | null | undefined,
  scope: SpeechRateOwnerScope | null | undefined,
): number {
  const isUnmarkedBotCall = scope?.source !== MERGEABLE_SESSION_SOURCE && !botOwnerParticipantIds(scope);
  if (isUnmarkedBotCall) return analysis?.totalAlerts || analysis?.alerts?.length || 0;
  return ownerSpeechRateAlerts(analysis?.alerts, scope).length;
}
